import moment from "moment-timezone";
import he from "he";

import Icon from "../components/main/icon";

export default class Misc {
    ordinal_suffix(i: number) {
        var j = i % 10,
            k = i % 100;
        if (j === 1 && k !== 11) {
            return i + "st";
        }
        if (j === 2 && k !== 12) {
            return i + "nd";
        }
        if (j === 3 && k !== 13) {
            return i + "rd";
        }
        if (i === 0) {
            return i;
        }
        return i + "th";
    }

    ucfirst(string: string) {
        if (typeof string === "undefined") {
            return "";
        }
        string = (string + "").toLowerCase();
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    to_duration(seconds: number) {
        let builder: any = [];
        let days = 0;
        let days_seconds = 0;
        let hours = 0;
        let hours_seconds = 0;
        let minutes = 0;
        let minutes_seconds = 0;

        //days
        if (seconds >= 86400) {
            days = Math.floor(seconds / 86400);
            days_seconds = days * 86400;

            if (days === 1) {
                builder.push(`${days}day`);
            } else {
                builder.push(`${days}days`);
            }
        }

        //hours
        if (seconds >= 3600) {
            hours = Math.floor((seconds - days_seconds) / 3600);
            hours_seconds = hours * 3600;

            if (hours === 1) {
                builder.push(`${hours}hour`);
            } else {
                builder.push(`${hours}hours`);
            }
        }

        //minutes
        if (seconds >= 60) {
            minutes = Math.floor((seconds - days_seconds - hours_seconds) / 60);
            minutes_seconds = minutes * 60;

            if (minutes === 1) {
                builder.push(`${minutes}min`);
            } else {
                builder.push(`${minutes}mins`);
            }
        }

        seconds = seconds - days_seconds - hours_seconds - minutes_seconds;
        if (seconds !== 0) {
            builder.push(`${seconds.toFixed(0)}s`);
        } else {
            builder.push(`0s`);
        }

        builder = builder.join(" ");

        return builder;
    }

    to_duration_minutes(seconds: number) {
        let builder: any = [];

        //year
        const years = Math.floor(seconds / Number(365 * 86400));
        seconds = seconds - Number(years * Number(365 * 86400));
        if (years > 0) {
            builder.push(`${years}y`);
        }

        // month
        const months = Math.floor(seconds / Number(30 * 86400));
        seconds = seconds - months * Number(30 * 86400);
        if (months > 0) {
            builder.push(`${months}mo`);
        }

        // day
        const days = Math.floor(seconds / Number(86400));
        seconds = seconds - days * Number(86400);
        if (days > 0) {
            builder.push(`${days}d`);
        }

        // hour
        const hours = Math.floor(seconds / Number(3600));
        seconds = seconds - hours * Number(3600);
        if (hours > 0) {
            builder.push(`${hours}h`);
        }

        // minute
        const minutes = Math.floor(seconds / Number(60));
        seconds = seconds - minutes * Number(60);
        if (minutes > 0) {
            builder.push(`${minutes}m`);
        }

        builder = builder.join(" ");

        return builder;
    }

    to_duration_seconds(seconds: number) {
        const secondsInMinute = 60;
        const secondsInHour = secondsInMinute * 60;
        const secondsInDay = secondsInHour * 24;
        const secondsInMonth = secondsInDay * 30; // Approximation of 30 days per month
        const secondsInYear = secondsInDay * 365; // Approximation of 365 days per year

        const years = Math.floor(seconds / secondsInYear);
        seconds %= secondsInYear;

        const months = Math.floor(seconds / secondsInMonth);
        seconds %= secondsInMonth;

        const days = Math.floor(seconds / secondsInDay);
        seconds %= secondsInDay;

        const hours = Math.floor(seconds / secondsInHour);
        seconds %= secondsInHour;

        const minutes = Math.floor(seconds / secondsInMinute);
        seconds %= secondsInMinute;

        let result = [];

        if (years > 0) result.push(`${years} year(s)`);
        if (months > 0) result.push(`${months} month(s)`);
        if (days > 0) result.push(`${days} day(s)`);
        if (hours > 0) result.push(`${hours} hour(s)`);
        if (minutes > 0) result.push(`${minutes} min(s)`);
        if (seconds > 0 || result.length === 0) result.push(`${Math.round(seconds)} sec(s)`);

        return result.join(", ");
    }

    shorten_date_time(date: number, timezone: string, is_today: boolean = false) {
        if (new Date(date).getTime() <= new Date().getTime() - 86400000 || moment.tz(Number(date), timezone).format("MMM Do YYYY") !== moment.tz(new Date().getTime(), timezone).format("MMM Do YYYY")) {
            return moment.tz(Number(date), timezone).format("MMM Do YYYY");
        } else {
            return `${is_today ? "today at " : ""}${new Date(Number(date)).getHours()}:${new Date(Number(date)).getMinutes() < 10 ? "0" + new Date(Number(date)).getMinutes() : new Date(Number(date)).getMinutes()}`;
        }
    }

    shorten_date_time_time(date: number, timezone: string, is_today: boolean = false) {
        if (new Date(date).getTime() <= new Date().getTime() - 86400000 || moment.tz(Number(date), timezone).format("MMM Do YYYY") !== moment.tz(new Date().getTime(), timezone).format("MMM Do YYYY")) {
            return moment.tz(Number(date), timezone).format("MMM Do YYYY HH:mm");
        } else {
            return `${is_today ? "today at " : ""}${new Date(Number(date)).getHours()}:${new Date(Number(date)).getMinutes() < 10 ? "0" + new Date(Number(date)).getMinutes() : new Date(Number(date)).getMinutes()}`;
        }
    }

    readable_date(date: number | string, timezone: string) {
        return moment.tz(Number(date), timezone).format("MMMM Do YYYY z");
    }

    readable_date_time(date: number | string, timezone: string) {
        return moment.tz(Number(date), timezone).format("MMMM Do YYYY h:mm a z");
    }

    readable_date_time_file_name(date: number | string, timezone: string) {
        return moment.tz(Number(date), timezone).format("MMMM Do YYYY h mm a z");
    }

    readable_date_time_seconds(date: number | string, timezone: string, ms: boolean = false) {
        if (ms === true) {
            return moment.tz(Number(date), timezone).valueOf();
        }
        return moment.tz(Number(date), timezone).format("MMMM Do YYYY h:mm:ss a z");
    }

    adjust_timezone(date: any = "", timezone: string = "") {
        return {
            offset: Number(moment.tz.zone(timezone)?.parse(date)) * 1000 * 60,
            abbr: moment.tz.zone(timezone)?.abbr(date),
        };
    }

    rand(min: number, max: number) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    card_img(card_type: string) {
        return card_type.toLowerCase() === "visa" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_visa" size="lg" />
        ) : card_type.toLowerCase() === "mastercard" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_mastercard" size="lg" />
        ) : card_type.toLowerCase() === "american express" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_amex" size="lg" />
        ) : card_type.toLowerCase() === "unionpay" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_unionpay" size="lg" />
        ) : card_type.toLowerCase() === "jcb" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_jcb" size="lg" />
        ) : card_type.toLowerCase() === "discover" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_discover" size="lg" />
        ) : card_type.toLowerCase() === "diners club" ? (
            <Icon className="ml-1 vertical-align-sub" icon="payment_diners" size="lg" />
        ) : (
            <></>
        );
    }

    thousand_seperator(number: number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    chat_output_replacer = (msg: string, redirects: [], a_href_color: string = "#FFFFFF") => {
        msg = msg.replace(/\\n/g, "<br>");
        msg = msg.replace(/\n/g, "<br>");
        for (let i = 0; i < redirects.length; i++) {
            msg = msg.replace(`${redirects[i]}`, `<a style="color: ${a_href_color} !important; border-color: ${a_href_color} !important" target="_blank" href="${redirects[i]}">${redirects[i]}</a>`);
        }
        return msg;
    };

    email_output_replacer = (body: any) => {
        if (body?.html?.length > 0) {
            return <iframe className="right-chat-view-iframe-max" srcDoc={he.decode(body.html || "")} />;
        }
        if (body?.text?.length > 0) {
            return <div dangerouslySetInnerHTML={{ __html: he.decode(body.text).replace(/\n/g, "<br>") }}></div>;
        }
        if (body?.unknown?.length > 0) {
            if (he.decode(body.unknown).toLowerCase().indexOf("!doctype") !== -1 || he.decode(body.unknown).toLowerCase().indexOf("<html") !== -1) {
                // is html
                return <iframe className="right-chat-view-iframe-max" srcDoc={he.decode(body.unknown || "")} />;
            } else {
                if (he.decode(body.unknown).toLowerCase().indexOf("> ") !== -1) {
                    // hide the replied/old emails attached to bottom of email
                    return <div dangerouslySetInnerHTML={{ __html: he.decode(body.unknown).split("> ")[0].replace(/\n/g, "<br>") }}></div>;
                } else {
                    return <div dangerouslySetInnerHTML={{ __html: he.decode(body.unknown).replace(/\n/g, "<br>") }}></div>;
                }
            }

            return "";
        }
        return "Unable to display email";
    };

    highlight_search_text = (text: any, search_term: any) => {
        if (!search_term) return <>{text}</>;

        const parts = text.split(new RegExp(`(${search_term})`, "gi"));
        return (
            <>
                {parts.map((part: any, index: number) =>
                    part.toLowerCase() === search_term.toLowerCase() ? (
                        <span key={index} style={{ backgroundColor: "#c7c3fe" }}>
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </>
        );
    };

    hex_to_rgba = (hex: any, alpha: any) => {
        let r = 0,
            g = 0,
            b = 0;

        // 3 digits
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
        }
        // 6 digits
        else if (hex.length === 7) {
            r = parseInt(hex[1] + hex[2], 16);
            g = parseInt(hex[3] + hex[4], 16);
            b = parseInt(hex[5] + hex[6], 16);
        }

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
}
